// Active-tournament store (DESIGN.md section 9.1 — Tournaments screen, 2026-08-31 round-8
// feedback item 21). Module-level like draft.ts/table.ts, so useGlobalScanner.ts (which has no
// view instance of its own) can read "is there an active tournament in registration?" and act on
// a badge scan without any screen-to-screen plumbing.
//
// "Active" = status 'registration' or 'in_progress' (MVP: at most one at a time — the backend
// enforces this on create, this store just reflects it). Tournaments.vue calls refresh() on
// mount and after every mutating action; useGlobalScanner.ts calls registerScannedPlayer() when
// a badge scan lands on the Tournaments screen during registration.
import { reactive } from 'vue'
import { addTournamentParticipant, getTournaments, ApiRequestError } from '../backend'
import { pushToast } from './toast'
import type { Player, Tournament } from '../types'

export const tournamentStore = reactive<{ active: Tournament | null; loading: boolean }>({
  active: null,
  loading: false,
})

export async function refreshActiveTournament(): Promise<void> {
  tournamentStore.loading = true
  try {
    const registration = await getTournaments('registration')
    const inProgress = registration.items.length === 0 ? await getTournaments('in_progress') : { items: [] }
    tournamentStore.active = registration.items[0] ?? inProgress.items[0] ?? null
  } catch {
    // Tournaments.vue surfaces its own load error; this store just leaves `active` as-is on a
    // transient failure rather than flashing to null.
  } finally {
    tournamentStore.loading = false
  }
}

/** useGlobalScanner.ts calls this when a badge scan lands on the Tournaments screen while a
 * tournament is in 'registration' — adds the scanning player as a participant, mirroring
 * "Scan to register" in the UI itself. Outside registration (no active tournament, or the
 * bracket's already generated) it's a no-op — the caller falls back to the generic scan toast. */
export async function registerScannedPlayer(player: Player, tournamentId?: number): Promise<boolean> {
  // Round-11: an explicit tournamentId (the detail screen's own tournament) wins; without one
  // (the list screen) fall back to the newest open tournament in the store. Registration-closed
  // errors on the explicit path just toast — the scan clearly meant THIS tournament.
  const t = tournamentId != null ? { id: tournamentId, name: tournamentStore.active?.id === tournamentId ? tournamentStore.active.name : 'the tournament' } : tournamentStore.active
  if (t == null) return false
  if (tournamentId == null && tournamentStore.active?.status !== 'registration') return false
  try {
    await addTournamentParticipant(t.id, player.id, 'scan')
    pushToast('success', `${player.displayName} registered for ${t.name}`, player)
  } catch (e) {
    if (e instanceof ApiRequestError && e.code === 'already_registered') {
      pushToast('success', `${player.displayName} is already registered`, player)
    } else if (tournamentId != null && e instanceof ApiRequestError && e.code === 'registration_closed') {
      return false // scanned on a started/finished tournament's detail — generic toast instead
    } else {
      pushToast('error', e instanceof ApiRequestError ? e.message : 'Could not register player')
    }
  }
  return true
}
